"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.APosOrderHoldSchema = exports.APosHoldCommEventSchema = exports.APosHoldItemSchema = exports.APosHoldItemStatusSchema = exports.APosOrderHoldStatusSchema = void 0;
const zod_1 = require("zod");
const timestamps_schema_1 = require("../../common/timestamps.schema");
const id_schema_1 = require("../../common/id.schema");
const location_schema_1 = require("./location.schema");
/**
 * Online-order HOLD model (additive — does NOT touch the production
 * APosOrders / sale-pipeline flow).
 *
 * A held online order lives in the separate order-holds table BETWEEN
 * checkout and the sale pipeline. On place, `place-order` writes a hold here
 * (status `pending_review`) instead of calling `/sales/complete`. A store
 * manager reviews stock, marks per-item status (substitute / add / out of
 * stock), messages the customer, optionally reassigns to another store, then
 * finalizes — at which point the UNCHANGED `/sales/complete` runs and the real
 * order is created. `pipelinePayload` is the exact body replayed on finalize.
 */
// Hold lifecycle status.
exports.APosOrderHoldStatusSchema = zod_1.z.enum([
    'pending_review', // just placed — awaiting store review/picking
    'ready', // reviewed/picked — ready to finalize
    'finalized', // handed to the sale pipeline; real order created
    'cancelled', // store/customer cancelled before finalize
]);
// Per-line fulfillment status set by the store manager during review.
exports.APosHoldItemStatusSchema = zod_1.z.enum([
    'in_stock',
    'out_of_stock',
    'substituted',
    'added',
]);
exports.APosHoldItemSchema = zod_1.z.object({
    lineId: id_schema_1.EntityIdSchema,
    variantId: id_schema_1.EntityIdSchema,
    productId: id_schema_1.EntityIdSchema.optional(),
    displayName: zod_1.z.string().min(1),
    /**
     * Presentation fields carried from the cart so the store-review screen can show
     * what the shopper actually bought. Optional: holds written before these existed
     * simply have none, and the admin UI backfills from the catalog.
     */
    productName: zod_1.z.string().optional(),
    variantLabel: zod_1.z.string().optional(),
    imageUrl: zod_1.z.string().optional(),
    sku: zod_1.z.string().optional(),
    quantity: zod_1.z.number().int().nonnegative(), // requested qty (0 when OOS-removed)
    unitPrice: zod_1.z.number().nonnegative(),
    lineTotal: zod_1.z.number().nonnegative(),
    costPrice: zod_1.z.number().nonnegative().optional(),
    taxIds: zod_1.z.array(zod_1.z.string()).optional(),
    feeIds: zod_1.z.array(zod_1.z.string()).optional(),
    compliancePresetId: zod_1.z.string().nullable().optional(),
    isAgeRestricted: zod_1.z.boolean().optional(),
    // Review fields (mutated by the item-status endpoint).
    itemStatus: exports.APosHoldItemStatusSchema.default('in_stock'),
    substituteForLineId: id_schema_1.EntityIdSchema.optional(), // line this one replaces
    note: zod_1.z.string().max(500).optional(),
});
// A logged customer/store communication on a hold (drives the comms trail).
exports.APosHoldCommEventSchema = zod_1.z.object({
    at: zod_1.z.number(),
    channel: zod_1.z.enum(['email', 'note']),
    direction: zod_1.z.enum(['to_customer', 'internal']),
    subject: zod_1.z.string().optional(),
    message: zod_1.z.string().max(4000),
    actorId: zod_1.z.string().optional(),
    actorName: zod_1.z.string().optional(),
});
exports.APosOrderHoldSchema = zod_1.z
    .object({
    tenantId: id_schema_1.TenantIdSchema,
    holdOrderId: id_schema_1.EntityIdSchema,
    orderNumber: zod_1.z.string().min(1),
    status: exports.APosOrderHoldStatusSchema.default('pending_review'),
    channel: zod_1.z.literal('online'),
    orderType: zod_1.z.enum(['online_pickup', 'online_delivery']),
    fulfillmentMethod: zod_1.z.enum(['pickup', 'delivery', 'shipping']).optional(),
    // Fulfilling store (reassignable). locationId is the LocationTimeIndex PK.
    locationId: id_schema_1.EntityIdSchema,
    fulfillingTenantId: id_schema_1.TenantIdSchema.optional(),
    // Customer
    customerId: id_schema_1.EntityIdSchema.optional(),
    customerName: zod_1.z.string().optional(),
    customerEmail: zod_1.z.string().email().optional(),
    customerPhone: zod_1.z.string().optional(),
    deliveryAddress: location_schema_1.APosAddressSchema.optional(),
    items: zod_1.z.array(exports.APosHoldItemSchema).min(1),
    subtotal: zod_1.z.number().nonnegative(),
    tax: zod_1.z.number().nonnegative().default(0),
    deliveryFee: zod_1.z.number().nonnegative().optional(),
    total: zod_1.z.number().nonnegative(),
    currency: zod_1.z.string().default('USD'),
    substitutionPolicy: zod_1.z.enum(['allow', 'refund_if_oos', 'contact_me']).optional(),
    customerNote: zod_1.z.string().max(2000).optional(),
    // Payment intent carried from checkout. Online = Dejavoo HPP (already
    // captured) or pay-on-delivery (cash/card on delivery, recorded only).
    paymentMode: zod_1.z.enum(['online_dejavoo', 'online_magtek', 'pay_on_delivery']),
    paymentStatus: zod_1.z.enum(['captured', 'pending']).default('pending'),
    // The exact /sales/complete body to replay on finalize. Stored opaque so
    // the hold service never re-derives pricing and the sale pipeline stays
    // unchanged.
    pipelinePayload: zod_1.z.record(zod_1.z.any()),
    // Populated once finalized (links the hold to the real pipeline order).
    finalizedOrderId: zod_1.z.string().optional(),
    finalizedTransactionId: zod_1.z.string().optional(),
    finalizedAt: zod_1.z.number().optional(),
    cancelledAt: zod_1.z.number().optional(),
    cancelReason: zod_1.z.string().optional(),
    comms: zod_1.z.array(exports.APosHoldCommEventSchema).default([]),
    receivedEmailsSentAt: zod_1.z.number().optional(),
    // Store branding + notification routing for themed customer/admin emails
    // (carried from checkout so pos-orders-service needn't read storefront settings).
    storeName: zod_1.z.string().optional(),
    storeThemeColor: zod_1.z.string().optional(),
    storeLogoUrl: zod_1.z.string().optional(),
    tenantNotifyEmail: zod_1.z.string().email().optional(),
})
    .merge(timestamps_schema_1.TimestampsSchema);
//# sourceMappingURL=order-hold.schema.js.map